/**
 * MarketplaceTab.jsx
 * 
 * Simplified marketplace management - just profile + service toggles
 */

import React, { useState } from 'react';
import useSWR from 'swr';
import TabPageLayout, { PageLoader, EmptyState } from './TabPageLayout';
import { Button } from './ui/button';
import { Label } from './ui/label';
import ServiceToggle from './ServiceToggle';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  Save,
  ExternalLink,
  Package,
  Check,
  Globe,
  Tag,
  Image as ImageIcon,
  Loader2,
  Upload,
  AlertCircle
} from 'lucide-react';
import { CATEGORY_OPTIONS } from '../constants/categories';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('jwtToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

const fetcher = async (url) => {
  const response = await fetch(url, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error('Failed to fetch');
  return response.json();
};

const MarketplaceTab = ({ organizationId }) => {
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingServiceId, setSavingServiceId] = useState(null);
  
  const [formData, setFormData] = useState({
    bio: '',
    logoUrl: '',
    website: ''
  });
  const [formInitialized, setFormInitialized] = useState(false);
  
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [pendingServiceId, setPendingServiceId] = useState(null);
  const [pendingCategory, setPendingCategory] = useState('');
  
  const { data: orgData, error: orgError, isLoading: orgLoading, mutate: mutateOrg } = useSWR(
    `${API_URL}/api/v2/auth/organizations/me`,
    fetcher,
    { 
      revalidateOnFocus: true,
      onSuccess: (data) => {
        if (!formInitialized) {
          setFormData({
            bio: data.marketplaceDescription || '',
            logoUrl: data.logoUrl || '',
            website: data.companyWebsite || ''
          });
          setFormInitialized(true);
        }
      }
    }
  );
  
  const { data: servicesData, error: servicesError, isLoading: servicesLoading, mutate: mutateServices } = useSWR(
    organizationId ? `${API_URL}/api/v2/organizations/${organizationId}/services?activeOnly=false` : null,
    fetcher,
    { revalidateOnFocus: true }
  );
  
  const services = servicesData?.services || [];
  const isLoading = orgLoading || servicesLoading;
  const hasError = orgError || servicesError;
  
  const saveMarketplaceProfile = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/auth/organizations/me`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          marketplaceEnabled: true, // Always enabled if they're here
          marketplaceDescription: formData.bio,
          companyWebsite: formData.website
        })
      });
      
      if (response.ok) {
        toast.success('Profile saved!');
        mutateOrg();
      } else {
        toast.error('Failed to save profile');
      }
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };
  
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_URL}/api/v2/auth/organizations/me/upload-logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadData
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, logoUrl: data.logoUrl }));
        toast.success('Logo uploaded!');
        mutateOrg();
      } else {
        toast.error('Failed to upload logo');
      }
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };
  
  const toggleServiceListing = async (serviceId, currentValue, hasCategory) => {
    if (!currentValue && !hasCategory) {
      setPendingServiceId(serviceId);
      setPendingCategory('');
      setCategoryModalOpen(true);
      return;
    }
    
    setSavingServiceId(serviceId);
    try {
      const response = await fetch(`${API_URL}/api/v2/organizations/${organizationId}/services/${serviceId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ marketplaceListed: !currentValue })
      });
      
      if (response.ok) {
        mutateServices();
        toast.success(!currentValue ? 'Listed on marketplace' : 'Removed from marketplace');
      } else {
        toast.error('Failed to update service');
      }
    } catch (error) {
      toast.error('Failed to update service');
    } finally {
      setSavingServiceId(null);
    }
  };

  const handleCategoryModalConfirm = async () => {
    if (!pendingCategory) {
      toast.error('Please select a category');
      return;
    }
    
    setSavingServiceId(pendingServiceId);
    try {
      const response = await fetch(`${API_URL}/api/v2/organizations/${organizationId}/services/${pendingServiceId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ category: pendingCategory, marketplaceListed: true })
      });
      
      if (response.ok) {
        mutateServices();
        toast.success('Service listed!');
        setCategoryModalOpen(false);
        setPendingServiceId(null);
        setPendingCategory('');
      } else {
        toast.error('Failed to update service');
      }
    } catch (error) {
      toast.error('Failed to update service');
    } finally {
      setSavingServiceId(null);
    }
  };

  const updateServiceCategory = async (serviceId, newCategory) => {
    setSavingServiceId(serviceId);
    try {
      const response = await fetch(`${API_URL}/api/v2/organizations/${organizationId}/services/${serviceId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ category: newCategory })
      });
      
      if (response.ok) {
        mutateServices();
        toast.success('Category updated!');
      } else {
        toast.error('Failed to update category');
      }
    } catch (error) {
      toast.error('Failed to update category');
    } finally {
      setSavingServiceId(null);
    }
  };
  
  if (isLoading) {
    return (
      <TabPageLayout title="Marketplace" subtitle="List your experiences on the BookingFlows marketplace">
        <PageLoader />
      </TabPageLayout>
    );
  }
  
  if (hasError) {
    return (
      <TabPageLayout title="Marketplace" subtitle="List your experiences on the BookingFlows marketplace">
        <EmptyState
          icon={<AlertCircle className="w-10 h-10" />}
          title="Unable to load data"
          description="Please refresh the page to try again"
        />
      </TabPageLayout>
    );
  }
  
  const listedCount = services.filter(s => s.marketplaceListed).length;
  
  return (
    <TabPageLayout 
      title="Marketplace" 
      subtitle="List your experiences on the BookingFlows marketplace"
      actions={
        <Button
          onClick={saveMarketplaceProfile}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white h-10 gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </Button>
      }
    >
      <div className="space-y-6 max-w-2xl">
        {/* Profile Section */}
        <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Business Profile</h3>
            <a 
              href={`/host/${organizationId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              View Profile
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          
          <div className="space-y-4">
            {/* Logo */}
            <div className="flex items-center gap-4">
              <div className="relative w-14 h-14 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden">
                {formData.logoUrl ? (
                  <img src={formData.logoUrl} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <ImageIcon className="w-5 h-5 text-gray-400" />
                )}
                {uploadingLogo && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                  </div>
                )}
              </div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button type="button" variant="outline" size="sm" className="gap-2 h-9" disabled={uploadingLogo} asChild>
                  <span>
                    <Upload className="w-4 h-4" />
                    {formData.logoUrl ? 'Change' : 'Upload'}
                  </span>
                </Button>
              </label>
            </div>
            
            {/* Bio */}
            <div>
              <Label className="text-sm">About</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Tell customers what makes your experiences special..."
                rows={2}
                className="mt-1 resize-none"
                maxLength={500}
              />
            </div>
            
            {/* Website */}
            <div>
              <Label className="text-sm flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-gray-400" />
                Website
              </Label>
              <Input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="yourwebsite.com"
                className="mt-1"
              />
            </div>
          </div>
        </div>
        
        {/* Services Section */}
        <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Experiences</h3>
            <span className="text-sm text-gray-500">{listedCount} listed</span>
          </div>
          
          {services.length === 0 ? (
            <p className="text-sm text-gray-500">Create services to list them on the marketplace</p>
          ) : (
            <div className="space-y-3">
              {services.filter(s => s.active !== false).map((service) => (
                <div 
                  key={service.id}
                  className="flex items-center justify-between gap-4 p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                      {service.images?.[0] ? (
                        <img src={service.images[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{service.name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>${service.basePrice || service.price}</span>
                        <span>•</span>
                        <span>{service.durationMinutes || service.duration} min</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {/* Always show category selector */}
                    <Select
                      value={service.category || ''}
                      onValueChange={(value) => updateServiceCategory(service.id, value)}
                      disabled={savingServiceId === service.id}
                    >
                      <SelectTrigger className="w-36 h-8 text-xs">
                        <SelectValue placeholder="Category">
                          {service.category ? (
                            <span className="flex items-center gap-1.5">
                              <span>{CATEGORY_OPTIONS.find(c => c.id === service.category)?.icon}</span>
                              <span>{CATEGORY_OPTIONS.find(c => c.id === service.category)?.name}</span>
                            </span>
                          ) : (
                            'Select category'
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORY_OPTIONS.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <span className="flex items-center gap-2">
                              <span>{cat.icon}</span>
                              <span>{cat.name}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {service.marketplaceListed && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Listed
                      </span>
                    )}
                    
                    <ServiceToggle
                      checked={service.marketplaceListed || false}
                      onChange={() => toggleServiceListing(service.id, service.marketplaceListed, !!service.category)}
                      disabled={savingServiceId === service.id}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Live Banner */}
        {listedCount > 0 && (
          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-2 text-green-800">
              <Check className="w-4 h-4" />
              <span className="font-medium">{listedCount} experience{listedCount > 1 ? 's' : ''} live on marketplace</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('/', '_blank')}
              className="gap-1.5 h-8 text-green-700 border-green-300 hover:bg-green-100"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View
            </Button>
          </div>
        )}
      </div>
      
      {/* Category Modal */}
      <Dialog open={categoryModalOpen} onOpenChange={setCategoryModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Select Category</DialogTitle>
            <DialogDescription>
              Choose a category to list this service
            </DialogDescription>
          </DialogHeader>
          
          <Select value={pendingCategory} onValueChange={setPendingCategory}>
            <SelectTrigger className="h-10">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  <span className="flex items-center gap-2">
                    <span>{cat.icon}</span>
                    <span>{cat.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCategoryModalOpen(false)} className="h-9">
              Cancel
            </Button>
            <Button
              onClick={handleCategoryModalConfirm}
              disabled={!pendingCategory || savingServiceId}
              className="bg-blue-600 hover:bg-blue-700 text-white h-9"
            >
              {savingServiceId ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              List
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
};

export default MarketplaceTab;
